const DESCRIPTION_MAX = 150;

function measurementId() {
  if (typeof window === "undefined") return "";
  const id = window.GA_MEASUREMENT_ID;
  return typeof id === "string" ? id.trim() : "";
}

function isValidMeasurementId(id) {
  return /^G-[A-Z0-9]+$/i.test(id);
}

function installGlobalErrorHandlers(gtagFn) {
  if (window.__eclipticaGa4ErrorHandlers) return;
  window.__eclipticaGa4ErrorHandlers = true;

  const sendException = (description, fatal, extra = {}) => {
    try {
      const msg = String(description || "error").slice(0, DESCRIPTION_MAX);
      gtagFn("event", "exception", {
        description: msg,
        fatal: Boolean(fatal),
        ...extra,
      });
    } catch (_) {
      // ignore
    }
  };

  window.addEventListener(
    "error",
    (event) => {
      if (!event) return;
      const desc = event.message || "Error";
      const src = event.filename ? String(event.filename).slice(0, 200) : "";
      sendException(desc, false, src ? { error_source: src } : {});
    },
    true
  );

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    const desc = reason instanceof Error ? reason.message : String(reason ?? "rejection");
    sendException(desc, false);
  });
}

let initialized = false;

/**
 * Loads gtag.js and configures GA4 when window.GA_MEASUREMENT_ID is set (e.g. G-xxxxxxxxxx).
 */
export function initGa4() {
  if (initialized) return;
  const id = measurementId();
  if (!id || !isValidMeasurementId(id)) return;

  initialized = true;

  // Layout may already load the official <head> gtag snippet (so Google’s installer detects it).
  if (typeof window.gtag === "function") {
    installGlobalErrorHandlers(window.gtag);
    return;
  }

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  gtag("js", new Date());
  gtag("config", id, { send_page_view: false });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);

  installGlobalErrorHandlers(gtag);
}

/**
 * SPA / multi-screen: send page_view with path, title, and a stable screen id (maps to GA4 screen class when configured).
 * Call after route changes; initial load is handled by AppShell.
 *
 * @param {{ pagePath?: string, pageTitle?: string, pageLocation?: string, screenClass?: string }} [opts]
 */
export function gaTrackPageView(opts = {}) {
  const id = measurementId();
  if (!id || !isValidMeasurementId(id)) return;
  if (typeof window.gtag !== "function") return;
  if (typeof window === "undefined" || typeof document === "undefined") return;

  const pagePath =
    opts.pagePath != null && String(opts.pagePath).trim() !== ""
      ? String(opts.pagePath)
      : `${window.location.pathname}${window.location.search}`;
  const pageTitle = opts.pageTitle != null ? String(opts.pageTitle) : document.title || "";
  const pageLocation = opts.pageLocation != null ? String(opts.pageLocation) : window.location.href;
  const screenClass = opts.screenClass != null ? String(opts.screenClass) : "";

  try {
    const payload = {
      page_path: pagePath,
      page_title: pageTitle,
      page_location: pageLocation,
    };
    if (screenClass) payload.screen_class = screenClass;
    window.gtag("event", "page_view", payload);
  } catch (_) {
    // ignore
  }
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function gaTrackEvent(name, params = {}) {
  const id = measurementId();
  if (!id || !isValidMeasurementId(id)) return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", name, params);
  } catch (_) {
    // ignore
  }
}

const CTA_PARAM_MAX = 100;

function trimParam(value, maxLen) {
  return String(value ?? "").trim().slice(0, maxLen);
}

/**
 * High-value CTA clicks (intent, not completed sign_up/login).
 * @param {{
 *   cta_id: string,
 *   cta_label?: string,
 *   destination?: string,
 *   screen_class?: string,
 *   product_code?: string,
 *   product_type?: string,
 * }} opts
 */
export function gaTrackCta(opts = {}) {
  const ctaId = trimParam(opts.cta_id, 80);
  if (!ctaId) return;
  const payload = { cta_id: ctaId };
  const label = trimParam(opts.cta_label, CTA_PARAM_MAX);
  if (label) payload.cta_label = label;
  const dest = trimParam(opts.destination, 200);
  if (dest) payload.destination = dest;
  const screen = trimParam(opts.screen_class, 64);
  if (screen) payload.screen_class = screen;
  const code = trimParam(opts.product_code, 64);
  if (code) payload.product_code = code;
  const ptype = trimParam(opts.product_type, 32);
  if (ptype) payload.product_type = ptype;
  gaTrackEvent("cta_click", payload);
}

/**
 * @param {{ signedIn?: boolean, user?: { id?: string, activeSubscription?: boolean } } | null} authContext
 */
export function gaSetUserFromAuth(authContext) {
  const id = measurementId();
  if (!id || !isValidMeasurementId(id) || typeof window.gtag !== "function") return;

  try {
    const user = authContext?.user;
    if (user?.id) {
      window.gtag("config", id, { user_id: String(user.id), send_page_view: false });
    }
    window.gtag("set", "user_properties", {
      subscription_active: user?.activeSubscription ? "yes" : "no",
      signed_in: authContext?.signedIn ? "yes" : "no",
    });
  } catch (_) {
    // ignore
  }
}
